import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import UserLayout from '../../components/user/UserLayout';
import api from '../../lib/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const toPretty = (sec = 0) => {
  const total = Number(sec) || 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
};

export default function UserAgentAnalyticsPage() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [agentId, setAgentId] = useState('');
  const [msg, setMsg] = useState('');

  const load = async (selectedAgentId = '') => {
    try {
      const params = selectedAgentId ? { agent_id: selectedAgentId } : {};
      const res = await api.get('/user/settings/agent-analytics/', { params });
      setData(res.data || null);
      setMsg('');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Unable to load agent analytics.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const trend = data?.trend?.[period] || [];
  const chartData = useMemo(() => ({
    labels: trend.map((r) => r.period),
    datasets: [
      {
        label: 'Open Chats',
        data: trend.map((r) => r.open),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217,119,6,.2)',
        tension: 0.35,
      },
      {
        label: 'Resolved Chats',
        data: trend.map((r) => r.resolved),
        borderColor: '#059669',
        backgroundColor: 'rgba(5,150,105,.2)',
        tension: 0.35,
      },
    ],
  }), [trend]);

  const kpis = data?.kpis || {};
  const activityDetails = data?.activity_details || [];

  return (
    <UserLayout title="Agent Analytics" subtitle="Track support agent performance and SLA status">
      {msg ? <Alert variant="info">{msg}</Alert> : null}
      {data?.supervisor_mode ? (
        <Card className="user-card p-3 mb-3">
          <Form.Group>
            <Form.Label className="mb-1">View agent analytics</Form.Label>
            <Form.Select
              value={agentId}
              onChange={(e) => {
                const nextId = e.target.value;
                setAgentId(nextId);
                load(nextId);
              }}
            >
              <option value="">All agents</option>
              {(data?.agents || []).map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card>
      ) : null}

      <Row className="g-3 mb-4">
        <Col md={4}><Card className="user-stat-card p-3"><div className="user-stat-label">Picked Chats</div><div className="user-stat-value">{kpis.picked_chats ?? kpis.assigned_chats ?? 0}</div></Card></Col>
        <Col md={4}><Card className="user-stat-card p-3"><div className="user-stat-label">Resolved Chats</div><div className="user-stat-value">{kpis.resolved_chats ?? 0}</div></Card></Col>
        <Col md={4}><Card className="user-stat-card p-3"><div className="user-stat-label">SLA Breaches</div><div className="user-stat-value">{kpis.sla_breach_count ?? 0}</div></Card></Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col md={6}><Card className="user-card p-4"><div className="user-section-title">Avg First Response</div><div className="display-6 fw-bold">{toPretty(kpis.avg_first_response_seconds)}</div></Card></Col>
        <Col md={6}><Card className="user-card p-4"><div className="user-section-title">Avg Resolution</div><div className="display-6 fw-bold">{toPretty(kpis.avg_resolution_seconds)}</div></Card></Col>
      </Row>

      <Card className="user-card p-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="user-section-title mb-0">Open vs Resolved Trend</div>
          <div className="d-flex gap-2">
            {['daily', 'weekly', 'monthly'].map((p) => (
              <button
                key={p}
                type="button"
                className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPeriod(p)}
              >
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <Line data={chartData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
      </Card>

      <Card className="user-card p-4 mt-4">
        <div className="user-section-title mb-2">Agent Activity Details</div>
        <div className="text-muted small mb-3">
          Per-agent timeline with picked/resolved/replied counts and direct links to live threads.
        </div>
        {activityDetails.length ? (
          activityDetails.map((row) => (
            <Card key={row?.agent?.id || row?.agent?.email} className="mb-3 border-0" style={{ background: '#f8fafc' }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div className="fw-semibold">
                    {row?.agent?.name || 'Agent'} ({row?.agent?.email || 'n/a'})
                  </div>
                  <div className="small text-muted d-flex align-items-center gap-2 flex-wrap">
                    <span>Picked: <strong>{row?.picked_count ?? 0}</strong> · Resolved: <strong>{row?.resolved_count ?? 0}</strong> · Replied: <strong>{row?.replied_count ?? 0}</strong></span>
                    {row?.agent?.id ? <Link to={`/dashboard/agent-activity/${row.agent.id}`}>View full activity</Link> : null}
                  </div>
                </div>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Handover</th>
                        <th>Channel</th>
                        <th>Status</th>
                        <th>Requester</th>
                        <th>Updated</th>
                        <th>Thread</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(row?.timeline || []).length ? (
                        row.timeline.map((item) => (
                          <tr key={`${row?.agent?.id}-${item.handover_id}`}>
                            <td>#{item.handover_id}</td>
                            <td>{item.channel}</td>
                            <td>{item.status}</td>
                            <td>{item.requester_name || item.requester_email || 'Guest'}</td>
                            <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                            <td>
                              <Link to={`/dashboard/agent-inbox?handover_id=${item.handover_id}`}>Open thread</Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-muted">No recent handover activity.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          ))
        ) : (
          <div className="text-muted">No activity details available yet.</div>
        )}
      </Card>
    </UserLayout>
  );
}
